import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Goal } from '../api/types'
import { useLiveRefresh } from '../lib/useLiveRefresh'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'

/** What you're aiming for this month, and where you actually are. Every number is
 *  counted from work recorded elsewhere — conversations logged, people moved to
 *  Appointment, contacts added — so a goal can't be "hit" without the work.
 *  Setting them happens on /goals, where the last three months are there to pick from. */
export function GoalsCard() {
  const { data, loading, error, reload } = useAsync(() => api.goals(), [])
  useLiveRefresh(reload)
  const goals = data?.goals ?? []

  return (
    <section className="card dashboard-goals" aria-label="Monthly goal tracker">
      <div className="card-head">
        <div className="dashboard-goals-title"><h2 className="h2">Monthly goals</h2><span>{data?.month}</span></div>
        <span className="spacer" />
        <Link className="btn sm" to="/goals">
          <Icon name={goals.length ? 'tune' : 'add'} size={15} /> {goals.length ? 'Edit goals' : 'Set goals'}
        </Link>
      </div>
      <div className="card-body dashboard-goal-grid">
        {error ? <div className="dashboard-goals-empty" role="alert"><p>Couldn’t load your goals.</p><button className="btn sm" onClick={reload}>Try again</button></div> : loading && !data ? <p className="quiet">Loading your monthly progress…</p> : goals.length === 0 ? (
          <div className="dashboard-goals-empty"><Icon name="flag" size={26} /><div><strong>Set your targets for the month</strong><p>Track conversations, appointments, and applications as you work.</p></div><Link className="btn" to="/goals">Set monthly goals <Icon name="arrow_forward" size={15} /></Link></div>
        ) : goals.map((g) => <GoalRow key={g.metric} goal={g} month={data?.month_key} />)}
      </div>
    </section>
  )
}

/** "Behind" is only worth saying while the month can still be saved, so the line
 *  switches to the plain shortfall once the goal is out of reach for the day. */
function paceLine(goal: Goal): string {
  const { ahead_by, on_pace } = goal.pace
  if (goal.done >= goal.target) return 'Goal reached'
  if (on_pace) return ahead_by > 0 ? `${ahead_by} ahead of pace` : 'On pace'
  return `${Math.abs(ahead_by)} behind pace`
}

/** Where a metric's own number came from, for the ones that have an answer. A
 *  figure you cannot open is a figure you have to take on trust. */
function behind(goal: Goal, month?: string): string | null {
  if (goal.metric === 'new_contacts' && month) return `/contacts?added=${month}`
  if (goal.metric === 'funded' || goal.metric === 'applications') return '/pipeline'
  return null
}

function GoalRow({ goal, month }: { goal: Goal; month?: string }) {
  const pct = goal.target ? Math.min(100, Math.round((goal.done / goal.target) * 100)) : 0
  const hit = goal.done >= goal.target
  const isBehind = !hit && !goal.pace.on_pace
  const to = behind(goal, month)
  const cls = `goal dashboard-goal${hit ? ' goal-hit' : ''}${isBehind ? ' goal-behind' : ''}${to ? ' goal-link' : ''}`
  const inner = (
    <>
      <div className="goal-top"><span className="goal-label">{goal.label}</span>{hit ? <Icon name="check_circle" size={17} /> : <Icon name="flag" size={16} />}</div>
      <div className="dashboard-goal-value"><strong>{goal.done}</strong><span>/ {goal.target}</span></div>
      <div className="goal-bar" role="progressbar" aria-label={goal.label} aria-valuenow={Math.min(goal.done, goal.target)} aria-valuemin={0} aria-valuemax={goal.target} aria-valuetext={`${goal.done} of ${goal.target}`}>
        <span style={{width:`${pct}%`}} />
        {/* Where the flat run-rate says you should be by tonight. */}
        {!hit && goal.pace.expected > 0 ? <i className="goal-pace-mark" style={{ left: `${Math.min(100, Math.round((goal.pace.expected / goal.target) * 100))}%` }} /> : null}
      </div>
      <div className="dashboard-goal-foot"><span>{pct}% complete</span><span>{paceLine(goal)}</span></div>
    </>
  )
  return to
    ? <Link to={to} className={cls} title={`Open the ${goal.label.toLowerCase()} behind this`}>{inner}</Link>
    : <div className={cls}>{inner}</div>
}
